import { apiFetch, getAuthHeader, handleUnauthorized, downloadBlob } from './core'

export interface ValidationIssue {
  level: 'ERROR' | 'WARNING' | 'CODELIST_WARNING'
  line: number
  column: number
  message: string
  label: string | null
}

export interface ValidatorField {
  label: string
  value: string | null
  line: number | null
  group: 'header' | 'leverancier' | 'koper' | 'regels' | 'totalen'
}

export interface ValidateResult {
  standard: string
  document_type: string
  is_valid: boolean
  xsd_available: boolean
  issues: ValidationIssue[]
  fields: ValidatorField[]
  xml_content: string
  original_xml_content: string
  ai_summary: string | null
  ai_anomalies: string[]
  pdf_base64: string | null
  pdf_filename: string | null
  saved_session_id: number | null
}

export interface ValidatorSessionSummary {
  id: number
  created_at: string
  uploaded_by: string
  filename: string
  file_size: number | null
  standard: string
  document_type: string
  is_valid: boolean
  error_count: number
  warning_count: number
  has_pdf: boolean
}

export interface ValidatorSessionDetail extends ValidatorSessionSummary {
  issues: ValidationIssue[]
}

export async function validateXml(file: File): Promise<ValidateResult> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch('/api/validate', {
    method: 'POST',
    headers: { Authorization: getAuthHeader() },
    body: form,
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  return res.json()
}

export interface EmailAttachmentResult {
  filename: string
  file_size: number
  status: 'ok' | 'error'
  result: ValidateResult | null
  error_code: 'unreadable_attachment' | 'validation_failed' | null
  error: string | null
}

export interface ValidateEmailResult {
  items: EmailAttachmentResult[]
  skipped_count: number
  depth_limit_reached: boolean
}

/** Fout van het mail-ingest endpoint; `code` is stabiel en wordt in de UI vertaald. */
export class ValidateEmailError extends Error {
  code: string | null
  constructor(code: string | null, message: string) {
    super(message)
    this.code = code
  }
}

export function isEmailContainer(name: string): boolean {
  const lower = name.toLowerCase()
  return lower.endsWith('.eml') || lower.endsWith('.msg')
}

/** Upload een .eml/.msg; de server pakt XML/PDF-bijlagen uit en valideert elk afzonderlijk. */
export async function validateEmail(file: File): Promise<ValidateEmailResult> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch('/api/validate/email', {
    method: 'POST',
    headers: { Authorization: getAuthHeader() },
    body: form,
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    const detail = body.detail
    if (detail && typeof detail === 'object') {
      throw new ValidateEmailError(detail.code ?? null, detail.message ?? res.statusText)
    }
    throw new ValidateEmailError(null, typeof detail === 'string' ? detail : res.statusText)
  }
  return res.json()
}

export async function getValidatorHistory(): Promise<ValidatorSessionSummary[]> {
  return apiFetch<ValidatorSessionSummary[]>('/api/validator-history')
}

export async function getValidatorSession(id: number): Promise<ValidatorSessionDetail> {
  return apiFetch<ValidatorSessionDetail>(`/api/validator-history/${id}`)
}

export async function deleteValidatorSession(id: number): Promise<void> {
  return apiFetch<void>(`/api/validator-history/${id}`, { method: 'DELETE' })
}

export async function openValidatorSession(id: number): Promise<ValidateResult> {
  return apiFetch<ValidateResult>(`/api/validator-history/${id}/open`)
}

export async function downloadValidatorXml(id: number, filename: string): Promise<void> {
  const res = await fetch(`/api/validator-history/${id}/xml`, {
    headers: { Authorization: getAuthHeader() },
  })
  handleUnauthorized(res)
  if (!res.ok) throw new Error('XML niet beschikbaar')
  await downloadBlob(res, filename)
}

export interface SendValidatorEmailResult {
  sent: boolean
  to: string
}

export async function sendValidatorEmail(sessionId: number, toAddress?: string): Promise<SendValidatorEmailResult> {
  return apiFetch<SendValidatorEmailResult>(`/api/validate/${sessionId}/send-email`, {
    method: 'POST',
    body: JSON.stringify(toAddress ? { to_address: toAddress } : {}),
  })
}

export async function downloadValidatorPdf(id: number, filename: string): Promise<void> {
  const res = await fetch(`/api/validator-history/${id}/pdf`, {
    headers: { Authorization: getAuthHeader() },
  })
  handleUnauthorized(res)
  if (!res.ok) throw new Error('PDF niet beschikbaar')
  await downloadBlob(res, filename.replace('.xml', '.pdf'))
}
